import {
  addBreadcrumb,
  Breadcrumb,
  captureException,
  captureMessage,
  ErrorEvent,
  init as sentryInit,
  reactNativeTracingIntegration,
  reactNavigationIntegration,
  setContext,
  setTag,
  setUser,
} from '@sentry/react-native';
import { BreadcrumbHint } from '@sentry/types';
import {
  getBuildNumber,
  getBundleId,
  getVersion,
} from 'react-native-device-info';
import { getExpoUpdateId } from 'shared/utils/appUpdateInfo/appUpdateInfo';
import { buildNumber } from 'shared/utils/appVersionUtils';
import getEnvironmentVar from 'shared/utils/getEnvironmentVar';
import { startNetworkStateTracking } from 'shared/utils/network/networkState';

import {
  blacklist,
  ErrorSeverity,
  FeatureArea,
  inAppBrowserInjectedFrames,
  inAppBrowserNames,
  inAppBrowserNoiseMessages,
  reactReconcilerDomMutationFrames,
  reactReconcilerDomNotFoundMessage,
  ResponsibleTeam,
  SENTRY_OTA_UPDATE_ID_USER_KEY,
  SentryTags,
  thirdPartyScriptLoadErrorSources,
} from './sentry.constants';
import setExpoUpdateContext from './setExpoUpdateContext';

const injectedFrameSet = new Set(inAppBrowserInjectedFrames);
const noiseMessageSet = new Set(inAppBrowserNoiseMessages);
const reactReconcilerFrameSet = new Set(reactReconcilerDomMutationFrames);

// A non-null updateId is the authoritative signal that an OTA bundle is running. Read at
// call time so the running update's identity is reflected in Sentry release/dist and the
// user tag. On embedded launches (no OTA) these fall back to the native build identity.
const getOtaUserTraits = (): Record<string, string> => {
  const activeUpdateId = getExpoUpdateId();
  return activeUpdateId
    ? { [SENTRY_OTA_UPDATE_ID_USER_KEY]: activeUpdateId }
    : {};
};

const isKnownThirdPartyScriptLoadSource = (
  host: string,
  pathname: string,
): boolean =>
  thirdPartyScriptLoadErrorSources.some(
    (source) =>
      source.host === host && pathname.startsWith(source.pathnamePrefix),
  );

const isThirdPartyScriptLoadError = (event: ErrorEvent): boolean => {
  return (
    event.exception?.values?.some((value) => {
      if (
        value.mechanism?.type !==
        'auto.browser.global_handlers.onunhandledrejection'
      ) {
        return false;
      }

      const errorMessage = value.value;
      if (
        typeof errorMessage !== 'string' ||
        !errorMessage.startsWith('Failed to load ')
      ) {
        return false;
      }

      const failedScriptUrl = errorMessage
        .replace('Failed to load ', '')
        .trim();

      try {
        const { host, pathname } = new URL(failedScriptUrl);
        return isKnownThirdPartyScriptLoadSource(host, pathname);
      } catch {
        return false;
      }
    }) ?? false
  );
};

const hasInjectedFrame = (event: ErrorEvent): boolean =>
  event.exception?.values?.some((value) =>
    value.stacktrace?.frames?.some(
      (frame) =>
        frame.function !== undefined && injectedFrameSet.has(frame.function),
    ),
  ) ?? false;

const isInAppBrowser = (event: ErrorEvent): boolean => {
  const browserName = event.contexts?.browser?.name;
  return (
    typeof browserName === 'string' && inAppBrowserNames.includes(browserName)
  );
};

// Drops NotFoundError crashes from React DOM's commit-phase deletion caused by
// external DOM mutation (browser extensions, in-app browser scripts) on
// React-managed nodes. Two match paths so we don't over-filter:
// - Reconciler frame path — reliable in dev/non-minified builds where the
//   React reconciler frame names survive.
// - WebKit-message path — needed because beforeSend runs client-side on the
//   pre-symbolication event and prod frames arrive minified (Xi/Ki/$i).
//   Narrowed with the unhandled onerror mechanism because React re-surfaces
//   commit-phase errors through captureCommitPhaseError → scheduler → task,
//   which always lands there. App-thrown NotFoundErrors (handled, or from a
//   Promise) keep being reported.
const isReactReconcilerDomNotFoundError = (event: ErrorEvent): boolean =>
  event.exception?.values?.some((value) => {
    if (value.type !== 'NotFoundError') {
      return false;
    }

    const hasReconcilerFrame =
      value.stacktrace?.frames?.some(
        (frame) =>
          frame.function !== undefined &&
          reactReconcilerFrameSet.has(frame.function),
      ) ?? false;

    if (hasReconcilerFrame) {
      return true;
    }

    return (
      value.value === reactReconcilerDomNotFoundMessage &&
      value.mechanism?.type === 'auto.browser.global_handlers.onerror' &&
      value.mechanism?.handled === false
    );
  }) ?? false;

const hasNoiseMessage = (event: ErrorEvent): boolean =>
  event.exception?.values?.some((value) => {
    const message = value.value;
    if (message === undefined) {
      return false;
    }
    return (
      noiseMessageSet.has(message) ||
      (value.type !== undefined &&
        noiseMessageSet.has(`${value.type}: ${message}`))
    );
  }) ?? false;

// Drops errors thrown by in-app browser (Instagram, Facebook, etc.) injected
// bridge scripts, which we cannot fix. Matches precisely on injected stack
// frames, and falls back to known-noise messages only within an in-app browser
// context so genuine errors from our own code are still reported.
const isInAppBrowserNoise = (event: ErrorEvent): boolean =>
  hasInjectedFrame(event) || (isInAppBrowser(event) && hasNoiseMessage(event));

const isAbortError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return error.name === 'AbortError';
  }

  if (typeof error === 'object' && error !== null && 'name' in error) {
    return error.name === 'AbortError';
  }

  return error === 'AbortError' || error === 'AbortError: AbortError';
};

// Sentry maps Error.name -> exception.type and Error.message -> exception.value,
// so matching on `type` (verified against @sentry/react-native's eventbuilder)
// works regardless of the AbortError's message text.
const isGlobalUnhandledRejection = (event: ErrorEvent): boolean =>
  event.exception?.values?.some(
    (value) =>
      value.mechanism?.type ===
        'auto.browser.global_handlers.onunhandledrejection' &&
      value.type === 'AbortError',
  ) ?? false;

const routingInstrumentation = reactNavigationIntegration({
  enableTimeToInitialDisplay: !__DEV__,
  routeChangeTimeoutMs: 1000,
  ignoreEmptyBackNavigationTransactions: true,
});

const init = () => {
  const activeUpdateId = getExpoUpdateId();
  const nativeRelease = `${getBundleId()}@${getVersion()}+${getBuildNumber()}`;

  sentryInit({
    dsn: getEnvironmentVar('EXPO_SENTRY_DSN'),
    debug: false,
    environment: getEnvironmentVar('EXPO_ENV'),
    //TODO: https://varomoney.atlassian.net/browse/JT-136434 ADD Sample rate function according to this https://docs.sentry.io/platforms/react-native/configuration/sampling/#setting-a-sampling-function
    tracesSampleRate: 0.7,
    // On OTA launches, distinguish each update as its own Sentry release + dist so distinct
    // OTA pushes on the same native binary are not collapsed together. Embedded launches
    // keep the native build identity. Symbolication is by Debug ID (Metro), so overriding
    // release/dist here does not affect source-map resolution.
    release: activeUpdateId
      ? `${nativeRelease}+update.${activeUpdateId}`
      : nativeRelease,
    dist: activeUpdateId ?? getBuildNumber(),
    // number of seconds the app can be unresponsive before reporting - default is 2
    appHangTimeoutInterval: 5,
    enableAppStartTracking: true,
    enableNativeFramesTracking: true,
    enableStallTracking: true,
    enableUserInteractionTracing: true,
    sendDefaultPii: false,
    tracePropagationTargets: [
      // Any endpoints we may hit that we want to trace
      'localhost',
      'uat-bank.varomoney.com',
      'bank.varomoney.com',
    ],
    integrations: [routingInstrumentation, reactNativeTracingIntegration()],
    beforeSend(event, hint) {
      const originalException = hint?.originalException as Error | undefined;
      const originalMessage = originalException?.message ?? '';

      if (blacklist[originalMessage]) {
        return null;
      }

      if (
        isAbortError(originalException) &&
        isGlobalUnhandledRejection(event)
      ) {
        return null;
      }

      if (isInAppBrowserNoise(event)) {
        return null;
      }

      if (isReactReconcilerDomNotFoundError(event)) {
        return null;
      }

      if (isThirdPartyScriptLoadError(event)) {
        return null;
      }

      return event;
    },
  });

  setTag(SentryTags.BUILD_NUMBER, buildNumber);
  setExpoUpdateContext();

  // Seed an OTA user tag so events captured before login are still attributed to the
  // active update. Preserved across login/logout by setUserId below.
  const otaUserTraits = getOtaUserTraits();
  if (Object.keys(otaUserTraits).length > 0) {
    setUser(otaUserTraits);
  }

  startNetworkStateTracking();
};

const logNonFatalError = (error: string | Error) => {
  if (error instanceof Error) {
    captureMessage(error.message, {
      level: 'warning',
    });
  } else {
    captureMessage(error, {
      level: 'warning',
    });
  }
};

const addTag = (key: SentryTags, value: string) => {
  try {
    setTag(key, value);
  } catch (error) {
    captureException(error);
  }
};

const logBreadcrumb = (breadcrumb: Breadcrumb, hint?: BreadcrumbHint) => {
  addBreadcrumb(breadcrumb, hint);
};

// Sentry's captureException only derives a stack trace and stable grouping from real
// Error instances. Handing it a plain object (e.g. a typed GraphQL error) makes Sentry
// synthesize a generic "Object captured as exception with keys: ..." event with no stack
// trace. Coerce non-Error values into a real Error, preserving a GraphQL __typename as the
// error name so grouping stays meaningful.
const toError = (value: unknown): Error => {
  if (value instanceof Error) {
    return value;
  }

  if (typeof value === 'object' && value !== null) {
    const { message, __typename } = value as {
      message?: unknown;
      __typename?: unknown;
    };
    const normalizedError = new Error(
      typeof message === 'string' && message.length > 0
        ? message
        : 'Non-Error object captured as exception',
    );
    if (typeof __typename === 'string' && __typename.length > 0) {
      normalizedError.name = __typename;
    }
    return normalizedError;
  }

  return new Error(typeof value === 'string' ? value : String(value));
};

/**
@deprecated Use logErrorWithContext() directly instead.
This function will be removed in a future version.
TODO: Update all usages to use logErrorWithContext() instead. JT-136304
*/
const logError = (error: unknown) => {
  if (isAbortError(error)) {
    return;
  }

  captureException(toError(error));
};

/**
 * Logs an error to Sentry with additional metadata and context.
 *
 * @param error - The error object or message to capture.
 * @param context - An object containing metadata to provide extra insight.
 *  - featureArea: Which feature area the error came from (e.g., 'Payments').
 *  - responsibleTeam: Which team is responsible for this code section.
 *  - severity: How severe the error is (e.g., 'critical', 'warning').
 *  - additionalContext?: A key-value map of extra custom data (strings, numbers, or any relevant values).
 *    You can add things like userIds, debug info, or any properties that help diagnose the problem.
 */
const logErrorWithContext = (
  error: unknown,
  context: {
    featureArea: FeatureArea;
    responsibleTeam: ResponsibleTeam;
    severity: ErrorSeverity;
    additionalContext?: Record<string, unknown>;
  },
) => {
  try {
    if (isAbortError(error)) {
      return;
    }

    setTag(
      SentryTags.ERROR_TYPE,
      error instanceof Error ? error.name : 'unknown',
    );
    setTag(SentryTags.ERROR_SEVERITY, context.severity);
    setTag(SentryTags.FEATURE_AREA, context.featureArea);
    setTag(SentryTags.RESPONSIBLE_TEAM, context.responsibleTeam);

    if (context.additionalContext) {
      setContext('error_details', context.additionalContext);
    }

    captureException(toError(error));
  } catch (err) {
    captureException(err);
  }
};

const setUserId = (userId: string | null) => {
  try {
    const otaUserTraits = getOtaUserTraits();
    if (userId) {
      setUser({ id: userId, ...otaUserTraits });
    } else if (Object.keys(otaUserTraits).length > 0) {
      setUser(otaUserTraits);
    } else {
      setUser(null);
    }
  } catch (error) {
    captureException(error);
  }
};

const setAttribute = (
  name: string,
  context: {
    [key: string]: unknown;
  } | null,
) => {
  try {
    setContext(name, context);
  } catch (error) {
    captureException(error);
  }
};

const setAttributes = (name: string, attributes: { [key: string]: string }) => {
  try {
    setContext(name, attributes);
  } catch (error) {
    captureException(error);
  }
};

export {
  addTag,
  init,
  logBreadcrumb,
  logError,
  logErrorWithContext,
  logNonFatalError,
  routingInstrumentation,
  setAttribute,
  setAttributes,
  setUserId,
};
